/**
 * Notification preferences — types, defaults, read/write/merge/validate helpers.
 *
 * Per-user notification delivery preferences stored as a serialized JSON
 * `valueString` extension on the active profile resource (Practitioner or
 * Patient). All access goes through the helpers in this file — UI
 * components must not parse raw extension JSON.
 *
 * Related concepts (do NOT mix):
 *   - Email templates: `ActivityDefinition` + `notification-template-config`
 *   - Runtime notifications: `Communication` with medium `in-app`
 *   - Automation rules: `PlanDefinition` (Rules Engine)
 */

import type { Practitioner, Patient } from '@medplum/fhirtypes';
import type { ProfileRoleCode } from '@unify/fhir-model/constants';
import { NOTIFICATION_PREFERENCES_EXT } from '@unify/fhir-model/constants';

import {
  getNotificationPreferences as getPractitionerPrefs,
} from '@unify/fhir-model/resources/practitioner';
import {
  getPatientNotificationPreferences as getPatientPrefs,
} from '@unify/fhir-model/resources/patient';

// ═══════════════════════════════════════════════════════════════════════
//  Types
// ═══════════════════════════════════════════════════════════════════════

export type NotificationChannel = 'in-app' | 'email';

export type ChannelStatus = 'active' | 'locked-on' | 'coming-soon';

/**
 * Per-category channel availability. Drives the settings UI:
 *   - `active`     — user can toggle
 *   - `locked-on`  — always enabled, toggle disabled (e.g. messages in-app)
 *   - `coming-soon` — not yet implemented, toggle disabled and OFF
 */
export const CHANNEL_AVAILABILITY: Record<
  NotificationPreferenceCategory,
  { inApp: ChannelStatus; email: ChannelStatus }
> = {
  messages:                { inApp: 'locked-on',   email: 'active' },
  'tasks-all':             { inApp: 'active',      email: 'active' },
  'tasks-priority':        { inApp: 'active',      email: 'active' },
  appointments:            { inApp: 'active',      email: 'active' },
  intake:                  { inApp: 'active',      email: 'active' },
  'episode-requests':      { inApp: 'active',      email: 'active' },
  'episode-transfers':     { inApp: 'active',      email: 'active' },
  assessments:             { inApp: 'active',      email: 'active' },
  'forms-uploaded':        { inApp: 'active',      email: 'active' },
  'forms-submitted':       { inApp: 'active',      email: 'active' },
  supervision:             { inApp: 'active',      email: 'coming-soon' },
  'supervision-notes-due': { inApp: 'active',      email: 'coming-soon' },
  billing:                 { inApp: 'coming-soon', email: 'coming-soon' },
  claims:                  { inApp: 'coming-soon', email: 'coming-soon' },
  payments:                { inApp: 'coming-soon', email: 'coming-soon' },
  'rules-engine':          { inApp: 'active',      email: 'active' },
  system:                  { inApp: 'active',      email: 'active' },
};

export const NOTIFICATION_CATEGORIES = [
  'messages',
  'tasks-all',
  'tasks-priority',
  'appointments',
  'intake',
  'episode-requests',
  'episode-transfers',
  'assessments',
  'forms-uploaded',
  'forms-submitted',
  'supervision',
  'supervision-notes-due',
  'billing',
  'claims',
  'payments',
  'rules-engine',
  'system',
] as const;

export type NotificationPreferenceCategory = (typeof NOTIFICATION_CATEGORIES)[number];

export interface NotificationPreferenceSetting {
  category: NotificationPreferenceCategory;
  channels: {
    inApp: boolean;
    email: boolean;
  };
  locked?: boolean;
  reason?: string;
}

export interface NotificationPreferencesConfig {
  schemaVersion: '1';
  role: ProfileRoleCode;
  settings: NotificationPreferenceSetting[];
  updatedAt?: string;
  updatedBy?: string;
}

// ═══════════════════════════════════════════════════════════════════════
//  Category labels & descriptions
// ═══════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════
//  Role → category mapping
// ═══════════════════════════════════════════════════════════════════════

const STAFF_TASK_CATEGORIES: NotificationPreferenceCategory[] = ['tasks-all', 'tasks-priority'];

const ROLE_CATEGORIES: Record<ProfileRoleCode, NotificationPreferenceCategory[]> = {
  client: [
    'messages',
    'appointments',
    'assessments',
    'forms-submitted',
    'episode-requests',
    'episode-transfers',
    'billing',
    'payments',
    'system',
  ],
  provider: [
    'messages',
    ...STAFF_TASK_CATEGORIES,
    'appointments',
    'intake',
    'episode-requests',
    'episode-transfers',
    'assessments',
    'forms-uploaded',
    'forms-submitted',
    'supervision',
    'system',
  ],
  supervisor: [
    'messages',
    ...STAFF_TASK_CATEGORIES,
    'appointments',
    'episode-transfers',
    'assessments',
    'forms-uploaded',
    'forms-submitted',
    'supervision',
    'supervision-notes-due',
    'system',
  ],
  'practice-owner': ['messages', ...STAFF_TASK_CATEGORIES, 'appointments', 'intake', 'episode-requests', 'billing', 'claims', 'payments', 'rules-engine', 'system'],
  'practice-admin': ['messages', ...STAFF_TASK_CATEGORIES, 'appointments', 'intake', 'episode-requests', 'billing', 'claims', 'payments', 'rules-engine', 'system'],
  'unify-admin': ['messages', ...STAFF_TASK_CATEGORIES, 'intake', 'episode-requests', 'billing', 'claims', 'payments', 'rules-engine', 'system'],
};

export const LOCKED_CATEGORIES: NotificationPreferenceCategory[] = ['system'];

// ═══════════════════════════════════════════════════════════════════════
//  Helpers — defaults
// ═══════════════════════════════════════════════════════════════════════

export function getCategoriesForRole(role: ProfileRoleCode): NotificationPreferenceCategory[] {
  return ROLE_CATEGORIES[role] ?? [];
}

const EMAIL_ON_BY_DEFAULT: ReadonlySet<NotificationPreferenceCategory> = new Set<NotificationPreferenceCategory>([
  'appointments',
  'assessments',
]);

function makeDefaultSetting(category: NotificationPreferenceCategory): NotificationPreferenceSetting {
  const isLocked = LOCKED_CATEGORIES.includes(category);
  const avail = CHANNEL_AVAILABILITY[category];
  return {
    category,
    channels: {
      inApp: avail?.inApp !== 'coming-soon',
      email: isLocked || (EMAIL_ON_BY_DEFAULT.has(category) && avail?.email !== 'coming-soon'),
    },
    ...(isLocked ? { locked: true, reason: 'Critical notifications cannot be disabled' } : {}),
  };
}

export function getDefaultPreferencesForRole(role: ProfileRoleCode): NotificationPreferencesConfig {
  return {
    schemaVersion: '1',
    role,
    settings: getCategoriesForRole(role).map(makeDefaultSetting),
  };
}

// ═══════════════════════════════════════════════════════════════════════
//  Helpers — merge
// ═══════════════════════════════════════════════════════════════════════

/** Maps retired preference categories to their replacements (channels copied to each). */
const DEPRECATED_CATEGORY_MIGRATIONS: Record<string, NotificationPreferenceCategory[]> = {
  tasks: ['tasks-all', 'tasks-priority'],
  'episode-updates': ['episode-transfers'],
  documents: ['forms-uploaded', 'forms-submitted'],
};

function expandLegacyStoredSettings(
  settings: NotificationPreferenceSetting[],
): NotificationPreferenceSetting[] {
  const expanded: NotificationPreferenceSetting[] = [];
  for (const s of settings) {
    const targets = DEPRECATED_CATEGORY_MIGRATIONS[s.category];
    if (targets) {
      for (const cat of targets) {
        expanded.push({ ...s, category: cat });
      }
    } else {
      expanded.push(s);
    }
  }
  return expanded;
}

/**
 * Merge stored preferences with role defaults so new categories appear
 * automatically after deploy and locked categories stay enforced.
 * Unknown stored categories are preserved in the settings array but
 * will not be displayed by the UI (no group mapping).
 */
export function mergeWithDefaults(
  stored: NotificationPreferencesConfig | undefined,
  role: ProfileRoleCode,
): NotificationPreferencesConfig {
  const defaults = getDefaultPreferencesForRole(role);
  if (!stored) return defaults;

  const storedMap = new Map(
    expandLegacyStoredSettings(stored.settings).map((s) => [s.category, s]),
  );
  const roleCategories = getCategoriesForRole(role);

  const merged: NotificationPreferenceSetting[] = roleCategories.map((cat) => {
    const def = defaults.settings.find((s) => s.category === cat)!;
    const existing = storedMap.get(cat);
    if (!existing) return def;
    if (LOCKED_CATEGORIES.includes(cat)) return def;
    return { ...def, channels: { ...existing.channels } };
  });

  // Preserve unknown categories from stored data (forward compat)
  const knownSet = new Set(roleCategories);
  for (const s of stored.settings) {
    if (!knownSet.has(s.category)) {
      merged.push(s);
    }
  }

  // Enforce channel availability: coming-soon → false, locked-on → true
  for (const s of merged) {
    const avail = CHANNEL_AVAILABILITY[s.category];
    if (!avail) continue;
    if (avail.inApp === 'coming-soon') s.channels.inApp = false;
    if (avail.email === 'coming-soon') s.channels.email = false;
    if (avail.inApp === 'locked-on') s.channels.inApp = true;
    if (avail.email === 'locked-on') s.channels.email = true;
  }

  return {
    ...defaults,
    settings: merged,
    updatedAt: stored.updatedAt,
    updatedBy: stored.updatedBy,
  };
}

// ═══════════════════════════════════════════════════════════════════════
//  Helpers — FHIR read/write (profile-polymorphic)
// ═══════════════════════════════════════════════════════════════════════

type ProfileResource = Practitioner | Patient;

export function readNotificationPreferences(
  profile: ProfileResource | undefined,
): NotificationPreferencesConfig | undefined {
  if (!profile) return undefined;
  if (profile.resourceType === 'Patient') {
    return getPatientPrefs<NotificationPreferencesConfig>(profile);
  }
  return getPractitionerPrefs<NotificationPreferencesConfig>(profile as Practitioner);
}

// ═══════════════════════════════════════════════════════════════════════
//  Helpers — Rules Engine integration query
// ═══════════════════════════════════════════════════════════════════════

/**
 * Given a profile, role, category, and channel, determine whether
 * delivery is enabled. Uses merged role defaults when nothing is stored.
 * If the category has no merged row (forward-compat / role slice gap),
 * in-app defaults on; email defaults on only for known taxonomy categories
 * so arbitrary codes do not imply email consent.
 */
export function isNotificationEnabled(
  profile: ProfileResource | undefined,
  role: ProfileRoleCode,
  category: NotificationPreferenceCategory,
  channel: NotificationChannel,
): boolean {
  const stored = readNotificationPreferences(profile);
  const config = mergeWithDefaults(stored, role);
  const setting = config.settings.find((s) => s.category === category);
  if (!setting) {
    if (channel === 'in-app') return true;
    return NOTIFICATION_CATEGORIES.includes(category);
  }
  return channel === 'in-app' ? setting.channels.inApp : setting.channels.email;
}

export { NOTIFICATION_PREFERENCES_EXT };
